import React, { useState } from 'react';
import { API, COLORS } from '../utils/constants';
import { r1, detectarTipoComida } from '../utils/helpers';
import ImagePicker from './ImagePicker';
import AddFoodModal from './AddFoodModal';
import AlimentoWizard from './AlimentoWizard';
import TodayLogPopup from './TodayLogPopup';

const GEO_ENABLED = false;

export default function QuickFoodCard({ nutrition, setNutrition }) {
  const [state, setState] = useState('idle'); // idle | loading
  const [toast, setToast] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [wizardSeed, setWizardSeed] = useState(null);
  const [showTodayLog, setShowTodayLog] = useState(false);
  const [editingFood, setEditingFood] = useState(null); // { index, food }
  const [tipoComidaForzado, setTipoComidaForzado] = useState(null);
  const [wizardBaseData, setWizardBaseData] = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const deleteFood = async (f, i) => {
    if (f.id) {
      await fetch(API + `/food-log/${f.id}`, { method: 'DELETE', credentials: 'include' }).catch(() => {});
    }
    const today = new Date().toISOString().split('T')[0];
    const newFoods = nutrition.foods.filter((_, j) => j !== i);
    const newNutrition = {
      calories: { ...nutrition.calories, current: newFoods.reduce((s, x) => s + x.calories, 0) },
      protein: { ...nutrition.protein, current: newFoods.reduce((s, x) => s + x.protein, 0) },
      carbs: { ...nutrition.carbs, current: newFoods.reduce((s, x) => s + x.carbs, 0) },
      fat: { ...nutrition.fat, current: newFoods.reduce((s, x) => s + x.fat, 0) },
      foods: newFoods,
    };
    setNutrition(newNutrition);
    await fetch(API + '/nutricion', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ fecha: today, calorias: newNutrition.calories.current, proteina: newNutrition.protein.current, carbos: newNutrition.carbs.current, grasa: newNutrition.fat.current }),
    }).catch(() => {});
  };

  const handlePhotoResult = (data) => {
    setState('idle');
    if (!data || data.calorias === undefined) { showToast('No se pudo analizar la foto'); return; }
    
    const tm = data.tipo_medida || 'peso';
    const pu = parseFloat(data.peso_unidad) || null;
    const qty = data.cantidad_sugerida || (tm === 'unidad' ? 1 : 100);
    
    let baseUnidadMode = null;
    if (tm === 'liquido') baseUnidadMode = { tipo: 'liquido', label: 'ml', pesoXUnidad: 1.0, densidad: 1.0 };
    else if (tm === 'unidad' && pu) baseUnidadMode = { tipo: 'unidad', label: 'unidades', pesoXUnidad: pu, densidad: 1.0 };

    let totalGrams = qty;
    if (baseUnidadMode?.tipo === 'unidad') totalGrams = qty * pu;
    
    const finalCals = (data.calorias / 100) * totalGrams;
    const finalProt = (data.proteina / 100) * totalGrams;
    const finalCarb = (data.carbos / 100) * totalGrams;
    const finalFat = (data.grasa / 100) * totalGrams;

    setWizardSeed({
      input: { name: data.nombre || '', calories: String(r1(finalCals)), protein: String(r1(finalProt)), carbs: String(r1(finalCarb)), fat: String(r1(finalFat)), grams: String(qty) },
      baseNutrients: { calories: data.calorias, protein: data.proteina, carbs: data.carbos, fat: data.grasa },
      baseUnidadMode: baseUnidadMode,
      tipoComida: detectarTipoComida(data.nombre || ''),
      pendingImagePath: data.image_path || null,
      fromPhoto: true,
      step: 2,
    });
    setAddModalOpen(true);
  };

  // Reordenar alimento a otra sección (drag & drop)
  const handleReorder = async (idx, newTipoComida, lastTime) => {
    const food = nutrition.foods[idx];
    if (!food) return;
    const newFoods = nutrition.foods.map((f, i) =>
      i === idx ? { ...f, tipo_comida: newTipoComida, time: lastTime.slice(0, 5) } : f
    );
    setNutrition({ ...nutrition, foods: newFoods });
    if (food.id) {
      await fetch(API + `/food-log/${food.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tipo_comida: newTipoComida, hora: lastTime }),
      }).catch(() => {});
    }
  };

  const foods = nutrition?.foods || [];

  return (
    <div style={{ marginBottom: 16 }}>
      {toast && <div style={{ position: 'fixed', top: 120, left: '50%', transform: 'translateX(-50%)', background: `linear-gradient(90deg, ${COLORS.accent}, #ff3366)`, color: '#fff', padding: '14px 28px', borderRadius: 30, fontSize: 14, fontWeight: 800, zIndex: 9999, boxShadow: "0 10px 30px rgba(0,0,0,0.5)" }}>{toast}</div>}

      <div style={{ background: "rgba(30, 30, 35, 0.4)", backdropFilter: "blur(20px)", borderRadius: 24, padding: "16px", border: `1px solid rgba(255,255,255,0.05)`, boxShadow: "0 8px 16px rgba(0,0,0,0.3)" }}>
        <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 800, margin: '0 0 12px', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: -0.5 }}>Ingesta diaria</h2>

        {state === 'loading' ? (
          <div style={{ padding: '20px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            <span style={{ color: COLORS.accent, fontSize: 14, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>⏳ Analizando...</span>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            {/* ✏️ TEXTO */}
            <button onClick={() => { setWizardSeed(null); setAddModalOpen(true); }} style={{ flex: 1, padding: '14px 0', borderRadius: 16, border: `1px solid rgba(255,255,255,0.05)`, background: 'rgba(255,255,255,0.04)', boxShadow: '0 4px 8px rgba(0,0,0,0.2)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 24 }}>✏️</span>
              <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, fontWeight: 700, letterSpacing: 0.3, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>TEXTO</span>
            </button>
            {/* 📷 FOTO */}
            <ImagePicker
              endpoint="/foto-comida"
              title="📷 Fotos del alimento"
              description="Añade todas las fotos del mismo alimento..."
              stripDataUrlPrefix={false}
              onResult={handlePhotoResult}
              onError={() => { setState('idle'); showToast('Error al analizar la foto'); }}
              onLoading={(l) => { if (l) setState('loading'); }}
            >
              {(triggerOpen) => (
                <button onClick={triggerOpen} style={{ flex: 1, padding: '14px 0', borderRadius: 16, border: `1px solid rgba(255,255,255,0.05)`, background: 'rgba(255,255,255,0.04)', boxShadow: '0 4px 8px rgba(0,0,0,0.2)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 24 }}>📷</span>
                  <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, fontWeight: 700, letterSpacing: 0.3, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>FOTO</span>
                </button>
              )}
            </ImagePicker>
            {/* 📋 HOY */}
            <button onClick={() => setShowTodayLog(true)} style={{ flex: 1, padding: '14px 0', borderRadius: 16, border: `1px solid rgba(255,255,255,0.05)`, background: 'rgba(255,255,255,0.04)', boxShadow: '0 4px 8px rgba(0,0,0,0.2)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 24 }}>📋</span>
              <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, fontWeight: 700, letterSpacing: 0.3, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>HOY</span>
            </button>
          </div>
        )}

      </div>

      <TodayLogPopup
        open={showTodayLog}
        onClose={() => setShowTodayLog(false)}
        foods={foods}
        nutrition={nutrition}
        onDelete={deleteFood}
        onEdit={(food, idx, date) => {
          setEditingFood({ index: idx, food, date });
          setWizardSeed(null);
          setAddModalOpen(true);
          setShowTodayLog(false);
        }}
        onReorder={handleReorder}
        onAddToSection={(tipoComida, date) => {
          setTipoComidaForzado(tipoComida);
          setWizardSeed({ tipoComida, forceTipoComida: true, targetDate: date });
          setEditingFood(null);
          setShowTodayLog(false);
          setAddModalOpen(true);
        }}
      />

      <AddFoodModal
        open={addModalOpen}
        onClose={() => {
          const volviaDeSeccion = !!tipoComidaForzado;
          setAddModalOpen(false);
          setWizardSeed(null);
          setEditingFood(null);
          setTipoComidaForzado(null);
          if (volviaDeSeccion) setShowTodayLog(true);
        }}
        nutrition={nutrition}
        setNutrition={setNutrition}
        onToast={(msg) => showToast(msg || 'Añadido correctamente')}
        seed={wizardSeed}
        editingFood={editingFood}
        onEditDone={() => { setEditingFood(null); setShowTodayLog(true); }}
        onOpenWizard={(datos) => {
          setAddModalOpen(false);
          setWizardBaseData(datos);
        }}
      />
      {wizardBaseData && (
        <AlimentoWizard
          modoNutricion={false}
          initialNombre={wizardBaseData.nombre || ''}
          initialData={wizardBaseData}
          onClose={() => setWizardBaseData(null)}
          onComplete={(data) => {
            setWizardBaseData(null);
            showToast('✅ Guardado en base de datos');
            if (data) {
              const tm = data.tipo_medida || 'peso';
              const pu = data.peso_unidad || null;
              let baseUnidadMode = null;
              if (tm === 'liquido') baseUnidadMode = { tipo: 'liquido', label: 'ml', pesoXUnidad: 1.0, densidad: data.densidad || 1.0 };
              else if (tm === 'unidad' && pu) baseUnidadMode = { tipo: 'unidad', label: 'unidades', pesoXUnidad: pu, densidad: 1.0 };
              const qty = data.cantidad || (tm === 'unidad' ? 1 : 100);
              const totalGrams = tm === 'unidad' && pu ? qty * pu : qty;
              setWizardSeed({
                input: { name: data.nombre, calories: String(Math.round((data.calorias/100)*totalGrams)), protein: String(Math.round((data.proteina/100)*totalGrams)), carbs: String(Math.round((data.carbos/100)*totalGrams)), fat: String(Math.round((data.grasa/100)*totalGrams)), grams: String(qty) },
                baseNutrients: { calories: data.calorias, protein: data.proteina, carbs: data.carbos, fat: data.grasa },
                baseUnidadMode,
                tipoComida: detectarTipoComida(data.nombre || ''),
                fromPhoto: false,
                step: 2,
              });
              setAddModalOpen(true);
            }
          }}
          zIndex={700}
        />
      )}
    </div>
  );
}
